import type { JudgePersona, SavedOutfit, ShowSummary } from '@runway/shared';
import { ApiError, api } from '../../net/api';

/**
 * Small fetch helpers for the home dashboard. Each tolerates the server-side
 * feature not being mounted yet (404/501 → empty) so the page never dead-ends.
 */

export type LoadState<T> = { data: T; loading: boolean; error: string | null; unavailable: boolean };

export function isUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function unwrap<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) return (data as Record<string, T[]>)[key] as T[];
  return [];
}

export async function fetchMyShows(): Promise<ShowSummary[]> {
  const res = await api.get<unknown>('/api/shows/mine');
  return unwrap<ShowSummary>(res, 'shows').sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function fetchOutfits(): Promise<SavedOutfit[]> {
  const res = await api.get<unknown>('/api/outfits');
  return unwrap<SavedOutfit>(res, 'outfits').sort((a, b) => (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt));
}

export async function fetchJudges(): Promise<JudgePersona[]> {
  const res = await api.get<unknown>('/api/judges');
  return unwrap<JudgePersona>(res, 'judges');
}

export type CreateShowInput = { theme: string; judgeId: string; dressSeconds: number };
export type CreateShowResult = { show: ShowSummary; roomId: string | null };

export async function createShow(input: CreateShowInput): Promise<CreateShowResult> {
  const res = await api.post<{ show?: ShowSummary; roomId?: string; id?: string } & Partial<ShowSummary>>('/api/shows', input);
  // Accept {show, roomId} (contract) or a bare ShowSummary.
  if (res && typeof res === 'object' && res.show) return { show: res.show, roomId: res.roomId ?? res.show.roomId ?? null };
  if (res && typeof res === 'object' && typeof res.id === 'string') {
    const show = res as ShowSummary;
    return { show, roomId: show.roomId ?? null };
  }
  throw new Error('The server did not return a show');
}

/** Status chip copy + tone per show phase. */
export function showStatusMeta(status: ShowSummary['status']): { label: string; tone: 'gold' | 'pink' | 'success' | 'lilac' | 'muted'; action: string; live: boolean } {
  switch (status) {
    case 'lobby':
      return { label: 'In the lobby', tone: 'gold', action: 'Rejoin', live: true };
    case 'dressing':
      return { label: 'Dressing up', tone: 'pink', action: 'Rejoin', live: true };
    case 'runway':
      return { label: 'On the runway', tone: 'pink', action: 'Watch', live: true };
    case 'results':
      return { label: 'Results in', tone: 'success', action: 'View results', live: false };
    case 'closed':
    default:
      return { label: 'Finished', tone: 'muted', action: 'View results', live: false };
  }
}
